import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  Clock,
  User,
  CheckCircle,
  Trash2,
  Eye,
  Lock,
  Edit,
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
} from "lucide-react";
import {
  format,
  isSameDay,
  isToday,
  parse,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  startOfDay,
} from "date-fns";
import { useDoctors } from "@/hooks/useDoctors";
import {
  getServiceName,
  getDoctorNameFlexible,
  getPatientDisplayName,
} from "@/utils/dataLookups";
import AppointmentContent from "./AppointmentContent";

/**
 * RoomView - Renders a room-column calendar layout (Jane App style).
 * One column per room, showing bookings filtered by doctor.
 */
const RoomView = ({
  selectedDate,
  roomCalendarData,
  appointmentsForSelectedDay,
  unavailabilityForSelectedDay,
  services,
  selectedDoctor,
  allDoctors,
  timeSlots,
  gridHeight,
  HOUR_HEIGHT,
  MINUTE_HEIGHT,
  timeSlotMinutes,
  startHour,
  onSlotClick,
  handleSlotClick,
  getAppointmentStyle,
  getAppointmentBackgroundStyle,
  getUnavailabilityStyle,
  calculateAppointmentColumns,
  getCurrentTimePosition,
  getDoctorColor,
  formatTime,
  formatAppointmentEndTime,
  onViewAppointment,
  onEditUnavailability,
  onDeleteUnavailability,
  currentTime,
}) => {
  // Build a service lookup for requiresDoctor check
  const serviceMap = useMemo(() => {
    const map = new Map();
    services.forEach((s) => {
      map.set(s.id, s);
    });
    return map;
  }, [services]);

  // Filter appointments based on doctor selection
  const filteredAppointments = useMemo(() => {
    if (!appointmentsForSelectedDay) return [];
    if (selectedDoctor === "all") return appointmentsForSelectedDay;

    return appointmentsForSelectedDay.filter((apt) => {
      // Include if this is the selected doctor's appointment
      if (apt.doctor_id === selectedDoctor) return true;
      // Include if service doesn't require a doctor (e.g., drip sessions)
      const service = serviceMap.get(apt.service_id);
      if (service && !service.requires_doctor) return true;
      return false;
    });
  }, [appointmentsForSelectedDay, selectedDoctor, serviceMap]);

  // Group appointments by room_id
  const appointmentsByRoom = useMemo(() => {
    const byRoom = new Map();
    filteredAppointments.forEach((apt) => {
      const roomId = apt.room_id || "__no_room__";
      const existing = byRoom.get(roomId) || [];
      existing.push(apt);
      byRoom.set(roomId, existing);
    });
    return byRoom;
  }, [filteredAppointments]);

  // Check if there are any "no room" appointments
  const noRoomAppointments = appointmentsByRoom.get("__no_room__") || [];

  // Build columns: rooms from calendar data + optional "No Room" column
  const columns = useMemo(() => {
    const cols = roomCalendarData.map((room) => ({
      id: room.id,
      name: room.name,
      availability: room.availability,
      services: room.services || [],
      type: "room",
    }));
    if (noRoomAppointments.length > 0) {
      cols.push({
        id: "__no_room__",
        name: "No Room",
        availability: null,
        services: [],
        type: "no_room",
      });
    }
    return cols;
  }, [roomCalendarData, noRoomAppointments.length]);

  // Compute effective time range from room availability (with 1-hour padding)
  const { effectiveStartHour, effectiveEndHour, roomTimeSlots, roomGridHeight } = useMemo(() => {
    let earliest = 24;
    let latest = 0;

    roomCalendarData.forEach((room) => {
      if (room.availability && room.availability.status === "active") {
        const [sh, sm] = room.availability.startTime.split(":").map(Number);
        const [eh, em] = room.availability.endTime.split(":").map(Number);
        const startDecimal = sh + sm / 60;
        const endDecimal = eh + em / 60;
        if (startDecimal < earliest) earliest = startDecimal;
        if (endDecimal > latest) latest = endDecimal;
      }
    });

    // Fallback if no active rooms
    if (earliest >= latest) {
      earliest = 8;
      latest = 18;
    }

    const effStart = Math.floor(earliest);
    const effEnd = Math.ceil(latest);

    // Generate time slots for this range
    const slots = [];
    for (let hour = effStart; hour < effEnd; hour++) {
      for (let minutes = 0; minutes < 60; minutes += timeSlotMinutes) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`;
        slots.push({
          time: timeString,
          hour,
          minutes,
          label: format(parse(timeString, "HH:mm", new Date()), "h:mm a"),
        });
      }
    }

    return {
      effectiveStartHour: effStart,
      effectiveEndHour: effEnd,
      roomTimeSlots: slots,
      roomGridHeight: (effEnd - effStart) * HOUR_HEIGHT,
    };
  }, [roomCalendarData, timeSlotMinutes, HOUR_HEIGHT]);

  // Recompute positioning functions for the room-specific time range
  const getRoomAppointmentStyle = (appointment, columnInfo) => {
    try {
      const startTime = new Date(appointment.appointment_date);
      const appointmentHour = startTime.getHours();
      const appointmentMinutes = startTime.getMinutes();

      let duration = 60;
      if (appointment.end_time) {
        const [endHour, endMinute] = appointment.end_time.split(":").map(Number);
        const endTotalMinutes = endHour * 60 + endMinute;
        const startTotalMinutes = appointmentHour * 60 + appointmentMinutes;
        duration = endTotalMinutes - startTotalMinutes;
      } else if (appointment.duration_actual) {
        duration = appointment.duration_actual;
      }

      const minutesFromGridStart =
        (appointmentHour - effectiveStartHour) * 60 + appointmentMinutes;
      const topPixels = minutesFromGridStart * MINUTE_HEIGHT;
      const heightPixels = duration * MINUTE_HEIGHT;

      let leftPosition = "1px";
      let rightPosition = "1px";
      let width = "auto";

      if (columnInfo) {
        const { column, totalColumns } = columnInfo;
        const columnWidth = 100 / totalColumns;
        const leftPercent = column * columnWidth;
        leftPosition = `calc(${leftPercent}% + 1px)`;
        width = `calc(${columnWidth}% - 2px)`;
        rightPosition = "auto";
      }

      const doctorColor = getDoctorColor(appointment);

      return {
        top: `${topPixels}px`,
        height: `${Math.max(heightPixels, 20)}px`,
        left: leftPosition,
        right: rightPosition,
        width: width,
        borderLeftColor: doctorColor,
      };
    } catch (error) {
      return { top: "0px", height: "30px", left: "1px", right: "1px" };
    }
  };

  const getRoomCurrentTimePosition = (date) => {
    if (!isToday(date)) return null;
    const now = new Date();
    const currentHourVal = now.getHours();
    const currentMinutes = now.getMinutes();
    if (currentHourVal < effectiveStartHour || currentHourVal >= effectiveEndHour) return null;
    const minutesFromStart = (currentHourVal - effectiveStartHour) * 60 + currentMinutes;
    const topPixels = minutesFromStart * MINUTE_HEIGHT;
    return { top: `${topPixels}px` };
  };

  const currentTimePosition = getRoomCurrentTimePosition(selectedDate);
  const slotHeight = HOUR_HEIGHT / (60 / timeSlotMinutes);

  // Format time for room headers
  const formatHeaderTime = (timeStr) => {
    if (!timeStr) return "";
    try {
      return format(parse(timeStr, "HH:mm", new Date()), "h:mm a");
    } catch {
      return timeStr;
    }
  };

  return (
    <div className="relative overflow-auto max-h-[calc(100vh-280px)]">
      {/* Sticky header row */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 flex">
        {/* Time label spacer */}
        <div className="w-14 flex-shrink-0 border-r border-slate-100" />
        {/* Room headers */}
        {columns.map((col, colIndex) => (
          <div
            key={`header-${col.id}`}
            className="border-r border-slate-100 px-2 py-2 min-w-[200px] flex-1"
            data-testid={`room-header-${colIndex}`}
          >
            <div className="font-semibold text-sm text-slate-800 truncate">
              {col.name}
            </div>
            {col.availability && col.availability.status === "active" && (
              <div className="text-xs text-slate-500">
                {formatHeaderTime(col.availability.startTime)} -{" "}
                {formatHeaderTime(col.availability.endTime)}
              </div>
            )}
            {col.services.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {col.services.slice(0, 3).map((svc) => (
                  <span
                    key={svc.id}
                    className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                  >
                    {svc.name}
                  </span>
                ))}
                {col.services.length > 3 && (
                  <span className="text-[10px] text-slate-400">
                    +{col.services.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Grid body: time labels + room columns */}
      <div className="flex pt-3">
        {/* Time labels column */}
        <div className="w-14 flex-shrink-0 sticky left-0 bg-white z-20 border-r border-slate-100">
          {roomTimeSlots.map((slot) => (
            <div
              key={`room-time-${slot.time}`}
              className="flex items-start justify-end pr-2"
              style={{ height: `${slotHeight}px` }}
            >
              {slot.minutes === 0 && (
                <span className="text-xs text-slate-400 font-normal -mt-2">
                  {format(parse(slot.time, "HH:mm", new Date()), "h a")}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Room columns */}
        {columns.map((col, colIndex) => {
          const roomAppointments = appointmentsByRoom.get(col.id) || [];
          const appointmentColumns = calculateAppointmentColumns(roomAppointments);

          return (
            <div
              key={`room-col-${col.id}`}
              className="relative border-r border-slate-100 min-w-[200px] flex-1"
              data-testid={`room-column-${colIndex}`}
            >
              <div className="relative" style={{ height: `${roomGridHeight}px` }}>
                {/* Time slot grid lines - clickable */}
                {roomTimeSlots.map((slot, index) => {
                  const isHourLine = slot.minutes === 0;

                  // Check if slot is within room operating hours
                  let isInOperatingHours = true;
                  if (col.availability && col.availability.status === "active") {
                    const slotMinutes = slot.hour * 60 + slot.minutes;
                    const [startH, startM] = col.availability.startTime.split(":").map(Number);
                    const [endH, endM] = col.availability.endTime.split(":").map(Number);
                    const opStart = startH * 60 + startM;
                    const opEnd = endH * 60 + endM;
                    isInOperatingHours = slotMinutes >= opStart && slotMinutes < opEnd;
                  }

                  return (
                    <div
                      key={`room-slot-${col.id}-${slot.time}`}
                      className={`absolute w-full ${
                        isHourLine
                          ? "border-b border-slate-200"
                          : "border-b border-slate-100 border-dashed"
                      } ${
                        isInOperatingHours
                          ? "bg-blue-50/20"
                          : "bg-slate-50/50"
                      } ${
                        isInOperatingHours && onSlotClick
                          ? "hover:bg-blue-50/50 cursor-pointer transition-colors z-5"
                          : ""
                      }`}
                      style={{
                        top: `${index * slotHeight}px`,
                        height: `${slotHeight}px`,
                      }}
                      onClick={() =>
                        isInOperatingHours &&
                        handleSlotClick(selectedDate, slot.time, col.type === "room" ? col.id : null)
                      }
                      title={
                        isInOperatingHours && onSlotClick
                          ? `Book in ${col.name} at ${slot.label}`
                          : ""
                      }
                      data-testid={`room-slot-${colIndex}-${slot.time}`}
                    />
                  );
                })}

                {/* Current time indicator */}
                {currentTimePosition && (
                  <div
                    className="absolute w-full z-20 pointer-events-none"
                    style={currentTimePosition}
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-red-500 rounded-full" />
                      <div className="flex-1 h-0.5 bg-red-500" />
                    </div>
                  </div>
                )}

                {/* Appointments in this room */}
                {roomAppointments.map((appointment, index) => {
                  const endTimeString = appointment.end_time
                    ? formatTime(
                        `${appointment.date}T${appointment.end_time}:00`
                      )
                    : formatAppointmentEndTime(
                        appointment.appointment_date,
                        appointment.duration_actual || 60
                      );
                  const columnInfo = appointmentColumns.get(appointment.id);

                  return (
                    <div
                      key={`room-apt-${appointment.id}`}
                      data-testid={`room-appointment-${colIndex}-${index}`}
                      className="absolute z-15 rounded-md p-2 border-l-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      style={{
                        ...getRoomAppointmentStyle(appointment, columnInfo),
                        ...getAppointmentBackgroundStyle(appointment),
                      }}
                      onClick={() =>
                        onViewAppointment && onViewAppointment(appointment)
                      }
                    >
                      <div className="flex items-center justify-between h-full overflow-hidden">
                        <div className="flex-1 min-w-0">
                          <AppointmentContent
                            appointment={appointment}
                            viewType="day"
                            columnInfo={columnInfo}
                            allDoctors={allDoctors}
                            endTimeString={endTimeString}
                          />
                        </div>
                        {appointment.status === "cancelled" &&
                          columnInfo?.totalColumns === 1 && (
                            <Badge
                              variant="destructive"
                              className="text-xs ml-2"
                            >
                              Cancelled
                            </Badge>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Show message if no rooms configured */}
        {columns.length === 0 && (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center text-slate-500">
              <LayoutGrid className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No rooms configured for this practice</p>
              <p className="text-sm text-slate-400 mt-1">
                Add rooms in Settings to use Room View
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TimeGridCalendar = ({
  selectedDate,
  appointments = [], // All filtered appointments (for week view)
  unavailability = [], // All filtered unavailability (for week view)
  appointmentsForSelectedDay = [], // Appointments for selected day only (for day view)
  unavailabilityForSelectedDay = [], // Unavailability for selected day only (for day view)
  services = [],
  doctors = [], // Doctors with colors
  rooms = [], // All rooms
  roomCalendarData = [], // Rooms enriched with availability + services for current day
  selectedDoctor = "all", // Doctor filter from Calendar.jsx
  onViewAppointment,
  onComplete,
  onDeleteAppointment,
  onDeleteUnavailability,
  onEditUnavailability,
  onDateSelect,
  onSlotClick, // New prop for handling slot clicks
  startHour = 8, // 8 AM
  endHour = 18, // 6 PM
  timeSlotMinutes = 30,
}) => {
  // Use doctors prop if provided, otherwise fetch them
  const { data: fetchedDoctors = [] } = useDoctors();
  const allDoctors = doctors.length > 0 ? doctors : fetchedDoctors;
  const currentTime = new Date();
  const [viewType, setViewType] = useState("day"); // "day" or "week"

  // Fixed pixel height per hour for alignment
  const HOUR_HEIGHT = 120; // pixels per hour
  const MINUTE_HEIGHT = HOUR_HEIGHT / 60; // pixels per minute

  // Helper function to get doctor color
  const getDoctorColor = (appointment) => {
    if (!appointment) return "#94a3b8"; // Default slate color

    // Try to find doctor by ID first
    if (appointment.doctor_id) {
      const doctor = allDoctors.find((d) => d.id === appointment.doctor_id);
      if (doctor?.color) return doctor.color;
    }

    // Try to find by name
    if (appointment.provider_name) {
      const doctor = allDoctors.find(
        (d) =>
          d.name === appointment.provider_name ||
          `${d.firstName} ${d.lastName}` === appointment.provider_name
      );
      if (doctor?.color) return doctor.color;
    }

    // Default color based on status or type
    if (appointment.status === "cancelled") return "#ef4444"; // Red for cancelled
    if (appointment.status === "completed") return "#10b981"; // Green for completed
    return "#3b82f6"; // Default blue
  };

  // Calculate the days to display based on view type
  const daysToDisplay = useMemo(() => {
    if (viewType === "day" || viewType === "room") {
      return [selectedDate];
    } else {
      // Week view - get all days of the week (Sunday start)
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 }); // Sunday start
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }
  }, [selectedDate, viewType]);

  // Week bar always shows the full week (Sunday to Saturday)
  const weekBarDays = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 }); // Sunday start
    const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [selectedDate]);

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minutes = 0; minutes < 60; minutes += timeSlotMinutes) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`;
        slots.push({
          time: timeString,
          hour,
          minutes,
          label: format(parse(timeString, "HH:mm", new Date()), "h:mm a"),
        });
      }
    }
    return slots;
  }, [startHour, endHour, timeSlotMinutes]);

  // Get appointments for a specific day
  const getAppointmentsForDay = (date) => {
    // For the selected day in day/room view, use pre-filtered appointments
    if ((viewType === "day" || viewType === "room") && appointmentsForSelectedDay) {
      return appointmentsForSelectedDay;
    }

    // For week view, filter from all appointments
    return (appointments || []).filter((apt) => {
      try {
        if (!apt?.appointment_date) return false;
        return isSameDay(new Date(apt.appointment_date), date);
      } catch (error) {
        return false;
      }
    });
  };

  // Get unavailability for a specific day
  const getUnavailabilityForDay = (date) => {
    // In day/room view, use the pre-filtered unavailability for the selected day if available
    if (
      (viewType === "day" || viewType === "room") &&
      isSameDay(date, selectedDate) &&
      unavailabilityForSelectedDay
    ) {
      return unavailabilityForSelectedDay;
    }
    // In week view or for other days, filter from all unavailability
    // Also fallback to this if unavailabilityForSelectedDay is not provided
    return (unavailability || []).filter((u) => {
      try {
        return u?.start_time && isSameDay(new Date(u.start_time), date);
      } catch (error) {
        return false;
      }
    });
  };

  // Helper function to check if two appointments overlap
  const appointmentsOverlap = (apt1, apt2) => {
    try {
      const start1 = new Date(apt1.appointment_date);
      const start2 = new Date(apt2.appointment_date);

      // Calculate end times
      let end1 = new Date(start1);
      let end2 = new Date(start2);

      if (apt1.end_time) {
        const [endHour1, endMinute1] = apt1.end_time.split(":").map(Number);
        end1.setHours(endHour1, endMinute1);
      } else {
        end1.setMinutes(start1.getMinutes() + (apt1.duration_actual || 60));
      }

      if (apt2.end_time) {
        const [endHour2, endMinute2] = apt2.end_time.split(":").map(Number);
        end2.setHours(endHour2, endMinute2);
      } else {
        end2.setMinutes(start2.getMinutes() + (apt2.duration_actual || 60));
      }

      // Check for overlap
      return start1 < end2 && start2 < end1;
    } catch (error) {
      return false;
    }
  };

  // Group overlapping appointments and assign columns
  const calculateAppointmentColumns = (appointments) => {
    const appointmentColumns = new Map();
    const groups = [];

    // Sort appointments by start time
    const sortedAppointments = [...appointments].sort((a, b) => {
      const dateA = new Date(a.appointment_date);
      const dateB = new Date(b.appointment_date);
      return dateA.getTime() - dateB.getTime();
    });

    // Group overlapping appointments
    sortedAppointments.forEach((apt) => {
      let addedToGroup = false;

      for (let group of groups) {
        // Check if this appointment overlaps with any in the group
        const overlapsWithGroup = group.some((groupApt) =>
          appointmentsOverlap(apt, groupApt)
        );

        if (overlapsWithGroup) {
          group.push(apt);
          addedToGroup = true;
          break;
        }
      }

      if (!addedToGroup) {
        groups.push([apt]);
      }
    });

    // Assign columns within each group
    groups.forEach((group) => {
      const columnsInUse = [];

      group.forEach((apt) => {
        // Find the first available column
        let column = 0;
        while (columnsInUse[column]) {
          // Check if this appointment overlaps with the appointment in this column
          const overlaps = columnsInUse[column].some((colApt) =>
            appointmentsOverlap(apt, colApt)
          );
          if (!overlaps) {
            break;
          }
          column++;
        }

        // Assign column
        if (!columnsInUse[column]) {
          columnsInUse[column] = [];
        }
        columnsInUse[column].push(apt);

        appointmentColumns.set(apt.id, {
          column,
          totalColumns: group.length > 1 ? Math.min(group.length, 3) : 1, // Max 3 columns to prevent too narrow appointments
          groupSize: group.length,
        });
      });
    });

    return appointmentColumns;
  };

  // Calculate position and height for appointments with column support
  const getAppointmentStyle = (appointment, columnInfo) => {
    try {
      const startTime = new Date(appointment.appointment_date);
      const appointmentHour = startTime.getHours();
      const appointmentMinutes = startTime.getMinutes();

      // Calculate duration more accurately
      let duration = 60; // Default to 60 minutes

      if (appointment.end_time) {
        // If we have an explicit end time, calculate from that
        const [endHour, endMinute] = appointment.end_time
          .split(":")
          .map(Number);
        const endTotalMinutes = endHour * 60 + endMinute;
        const startTotalMinutes = appointmentHour * 60 + appointmentMinutes;
        duration = endTotalMinutes - startTotalMinutes;
      } else if (appointment.duration_actual) {
        duration = appointment.duration_actual;
      }

      // Position from top (minutes from grid start hour)
      const minutesFromGridStart =
        (appointmentHour - startHour) * 60 + appointmentMinutes;
      const topPixels = minutesFromGridStart * MINUTE_HEIGHT;

      // Height based on duration
      const heightPixels = duration * MINUTE_HEIGHT;

      // Calculate horizontal position and width based on column info
      let leftPosition = "1px";
      let rightPosition = "1px";
      let width = "auto";

      if (columnInfo) {
        const { column, totalColumns } = columnInfo;
        const columnWidth = 100 / totalColumns;
        const leftPercent = column * columnWidth;

        leftPosition = `calc(${leftPercent}% + 1px)`;
        width = `calc(${columnWidth}% - 2px)`;
        rightPosition = "auto";
      }

      // Get doctor color for border
      const doctorColor = getDoctorColor(appointment);

      return {
        top: `${topPixels}px`,
        height: `${Math.max(heightPixels, 20)}px`, // Minimum height of 20px
        left: leftPosition,
        right: rightPosition,
        width: width,
        borderLeftColor: doctorColor,
      };
    } catch (error) {
      console.error("Error calculating appointment style:", error);
      return { top: "0px", height: "30px", left: "1px", right: "1px" };
    }
  };

  const getUnavailabilityStyle = (unavail) => {
    try {
      const startTime = new Date(unavail.start_time);
      const endTime = new Date(unavail.end_time);

      const startHourVal = startTime.getHours();
      const startMinutes = startTime.getMinutes();
      const endHourVal = endTime.getHours();
      const endMinutes = endTime.getMinutes();

      // Position from top
      const minutesFromDayStart =
        (startHourVal - startHour) * 60 + startMinutes;
      const topPixels = minutesFromDayStart * MINUTE_HEIGHT;

      // Duration in minutes
      const duration =
        (endHourVal - startHourVal) * 60 + (endMinutes - startMinutes);
      const heightPixels = duration * MINUTE_HEIGHT;

      return {
        top: `${topPixels}px`,
        height: `${Math.max(heightPixels, 30)}px`,
      };
    } catch (error) {
      console.error("Error calculating unavailability style:", error);
      return { top: "0px", height: "30px" };
    }
  };

  const formatTime = (dateTimeString) => {
    try {
      return format(new Date(dateTimeString), "h:mm a");
    } catch (error) {
      return "Invalid time";
    }
  };

  const formatAppointmentEndTime = (dateTimeString, durationMinutes = 60) => {
    try {
      const startDate = new Date(dateTimeString);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      return format(endDate, "h:mm a");
    } catch (error) {
      return "Invalid time";
    }
  };

  const statusColors = {
    scheduled: "bg-blue-50 border-l-blue-500 text-blue-900 hover:bg-blue-100",
    in_progress:
      "bg-yellow-50 border-l-yellow-500 text-yellow-900 hover:bg-yellow-100",
    completed:
      "bg-green-50 border-l-green-500 text-green-900 hover:bg-green-100",
    cancelled: "bg-red-50 border-l-red-500 text-red-900 hover:bg-red-100",
    no_show: "bg-gray-50 border-l-gray-500 text-gray-900 hover:bg-gray-100",
  };

  // Generate a light background color from the doctor's color
  const getAppointmentBackgroundStyle = (appointment) => {
    const doctorColor = getDoctorColor(appointment);

    // For cancelled appointments, use red
    if (appointment.status === "cancelled") {
      return { backgroundColor: "#fef2f2" }; // Light red
    }

    // For completed appointments, use green
    if (appointment.status === "completed") {
      return { backgroundColor: "#f0fdf4" }; // Light green
    }

    // Convert hex to RGB and create a light version
    const hex = doctorColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Create a light background by mixing with white (95% white, 5% color)
    const lightR = Math.round(r * 0.15 + 255 * 0.85);
    const lightG = Math.round(g * 0.15 + 255 * 0.85);
    const lightB = Math.round(b * 0.15 + 255 * 0.85);

    return {
      backgroundColor: `rgb(${lightR}, ${lightG}, ${lightB})`,
    };
  };

  // Current time indicator position
  const getCurrentTimePosition = (date) => {
    if (!isToday(date)) return null;

    const now = new Date();
    const currentHourVal = now.getHours();
    const currentMinutes = now.getMinutes();

    if (currentHourVal < startHour || currentHourVal >= endHour) return null;

    const minutesFromStart = (currentHourVal - startHour) * 60 + currentMinutes;
    const topPixels = minutesFromStart * MINUTE_HEIGHT;

    return { top: `${topPixels}px` };
  };

  // Total height for the grid
  const gridHeight = (endHour - startHour) * HOUR_HEIGHT;

  // Navigation functions
  const handlePrevious = () => {
    if (viewType === "day" || viewType === "room") {
      onDateSelect(subDays(selectedDate, 1));
    } else {
      onDateSelect(subWeeks(selectedDate, 1));
    }
  };

  const handleNext = () => {
    if (viewType === "day" || viewType === "room") {
      onDateSelect(addDays(selectedDate, 1));
    } else {
      onDateSelect(addWeeks(selectedDate, 1));
    }
  };

  const handleToday = () => {
    onDateSelect(new Date());
  };

  // Calculate column width for week view (room view handled separately by RoomView component)
  const columnWidth = viewType === "week" ? `${100 / 7}%` : "100%";

  // Handle slot click for booking
  const handleSlotClick = (date, time, roomId = null) => {
    if (!onSlotClick) return;

    // Check if there are any appointments or unavailability at this time
    const dayAppointments = getAppointmentsForDay(date);
    const dayUnavailability = getUnavailabilityForDay(date);

    // Parse the clicked time
    const [hours, minutes] = time.split(":").map(Number);
    const slotStart = new Date(date);
    slotStart.setHours(hours, minutes, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + timeSlotMinutes * 60000);

    // Check which doctors are occupied during this slot
    const occupiedDoctors = new Set();
    dayAppointments.forEach((apt) => {
      try {
        const aptStart = new Date(apt.appointment_date);
        let aptEnd = new Date(aptStart);

        if (apt.end_time) {
          const [endHour, endMinute] = apt.end_time.split(":").map(Number);
          aptEnd.setHours(endHour, endMinute);
        } else {
          aptEnd.setMinutes(
            aptStart.getMinutes() + (apt.duration_actual || 60)
          );
        }

        // If appointment overlaps with this slot, mark doctor as occupied
        if (slotStart < aptEnd && slotEnd > aptStart) {
          occupiedDoctors.add(apt.doctor_id);
        }
      } catch (error) {
        // ignore
      }
    });

    // Allow booking even if some doctors are busy - the booking form will handle doctor selection
    const hasConflict = false;

    // Check for conflicts with unavailability
    const hasUnavailability = dayUnavailability.some((unavail) => {
      try {
        const unavailStart = new Date(unavail.start_time);
        const unavailEnd = new Date(unavail.end_time);
        return slotStart < unavailEnd && slotEnd > unavailStart;
      } catch (error) {
        return false;
      }
    });

    // Only allow booking if no conflicts
    if (!hasConflict && !hasUnavailability) {
      const slotData = {
        date: date,
        time: format(slotStart, "HH:mm"),
        timeLabel: format(slotStart, "h:mm a"),
      };
      if (roomId) {
        slotData.roomId = roomId;
      }
      onSlotClick(slotData);
    }
  };

  const [calendarOpen, setCalendarOpen] = useState(false);

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            {viewType === "week"
              ? `${format(daysToDisplay[0], "MMM d")} - ${format(daysToDisplay[6], "MMM d, yyyy")}`
              : format(selectedDate, "MMMM d, yyyy")}
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Calendar Picker Popover */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="calendar-picker-btn">
                  <Calendar className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      onDateSelect(date);
                      setCalendarOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* View Type Selector */}
            <Select value={viewType} onValueChange={setViewType}>
              <SelectTrigger className="w-32" data-testid="view-type-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day" data-testid="day-view-option">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Day View
                  </div>
                </SelectItem>
                <SelectItem value="room" data-testid="room-view-option">
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4" />
                    Room View
                  </div>
                </SelectItem>
                <SelectItem value="week" data-testid="week-view-option">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    Week View
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Navigation */}
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={handlePrevious} data-testid="calendar-prev-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday} data-testid="calendar-today-btn">
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={handleNext} data-testid="calendar-next-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        {/* Horizontal Week Bar - Sun to Sat (only in week view) */}
        {viewType === "week" && (
          <div className="flex items-center border-t border-slate-200 bg-white" data-testid="week-bar">
            {weekBarDays.map((date, index) => {
              const isSelected = isSameDay(date, selectedDate);
              const isTodayDate = isToday(date);

              return (
                <button
                  key={`weekbar-${date.toISOString()}`}
                  onClick={() => onDateSelect(date)}
                  className={`flex-1 py-3 px-1 text-center transition-colors hover:bg-slate-50 ${
                    isSelected ? "border-b-2 border-b-blue-500 bg-blue-50/50" : "border-b-2 border-b-transparent"
                  }`}
                  data-testid={`weekbar-day-${index}`}
                >
                  <div className={`text-xs font-medium ${isTodayDate ? "text-blue-600" : "text-slate-400"}`}>
                    {format(date, "EEE")}
                  </div>
                  <div className={`text-xl font-semibold ${isSelected ? "text-blue-600" : isTodayDate ? "text-blue-600" : "text-slate-700"}`}>
                    {format(date, "d")}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {viewType === "room" ? (
          <RoomView
            selectedDate={selectedDate}
            roomCalendarData={roomCalendarData}
            appointmentsForSelectedDay={appointmentsForSelectedDay}
            unavailabilityForSelectedDay={unavailabilityForSelectedDay}
            services={services}
            selectedDoctor={selectedDoctor}
            allDoctors={allDoctors}
            timeSlots={timeSlots}
            gridHeight={gridHeight}
            HOUR_HEIGHT={HOUR_HEIGHT}
            MINUTE_HEIGHT={MINUTE_HEIGHT}
            timeSlotMinutes={timeSlotMinutes}
            startHour={startHour}
            onSlotClick={onSlotClick}
            handleSlotClick={handleSlotClick}
            getAppointmentStyle={getAppointmentStyle}
            getAppointmentBackgroundStyle={getAppointmentBackgroundStyle}
            getUnavailabilityStyle={getUnavailabilityStyle}
            calculateAppointmentColumns={calculateAppointmentColumns}
            getCurrentTimePosition={getCurrentTimePosition}
            getDoctorColor={getDoctorColor}
            formatTime={formatTime}
            formatAppointmentEndTime={formatAppointmentEndTime}
            onViewAppointment={onViewAppointment}
            onEditUnavailability={onEditUnavailability}
            onDeleteUnavailability={onDeleteUnavailability}
            currentTime={currentTime}
          />
        ) : (
        <div className="relative overflow-auto max-h-[calc(100vh-280px)]">
          {/* Time slots grid */}
          <div className="flex pt-3">
            {/* Time labels column - compact style: only show hour labels */}
            <div className="w-14 flex-shrink-0 sticky left-0 bg-white z-20 border-r border-slate-100">
              {timeSlots.map((slot, index) => (
                <div
                  key={slot.time}
                  className="flex items-start justify-end pr-2"
                  style={{
                    height: `${HOUR_HEIGHT / (60 / timeSlotMinutes)}px`,
                  }}
                >
                  {/* Only show label at hour marks (when minutes === 0) */}
                  {slot.minutes === 0 && (
                    <span className="text-xs text-slate-400 font-normal -mt-2">
                      {format(parse(slot.time, "HH:mm", new Date()), "h a")}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Calendar content - one column per day */}
            <div className="flex flex-1">
              {daysToDisplay.map((date, dayIndex) => {
                const dayAppointments = getAppointmentsForDay(date);
                const dayUnavailability = getUnavailabilityForDay(date);
                const currentTimePosition = getCurrentTimePosition(date);
                // Calculate column positions for overlapping appointments
                const appointmentColumns =
                  calculateAppointmentColumns(dayAppointments);

                return (
                  <div
                    key={date.toISOString()}
                    className={`relative ${viewType === "week" ? "border-r border-slate-100" : ""}`}
                    style={{
                      width: columnWidth,
                      minWidth: viewType === "week" ? "120px" : "auto",
                    }}
                  >
                    <div
                      className="relative"
                      style={{ height: `${gridHeight}px` }}
                    >
                      {/* Time slot grid lines - clickable for booking */}
                      {timeSlots.map((slot, index) => {
                        // Check if this slot has conflicts
                        const dayAppointments = getAppointmentsForDay(date);
                        const dayUnavailability = getUnavailabilityForDay(date);

                        const [hours, minutes] = slot.time
                          .split(":")
                          .map(Number);
                        const slotStart = new Date(date);
                        slotStart.setHours(hours, minutes, 0, 0);
                        const slotEnd = new Date(
                          slotStart.getTime() + timeSlotMinutes * 60000
                        );

                        // Check if ALL doctors are occupied (not just if ANY doctor is occupied)
                        const occupiedDoctors = new Set();
                        dayAppointments.forEach((apt) => {
                          try {
                            const aptStart = new Date(apt.appointment_date);
                            let aptEnd = new Date(aptStart);

                            if (apt.end_time) {
                              const [endHour, endMinute] = apt.end_time
                                .split(":")
                                .map(Number);
                              aptEnd.setHours(endHour, endMinute);
                            } else {
                              aptEnd.setMinutes(
                                aptStart.getMinutes() +
                                  (apt.duration_actual || 60)
                              );
                            }

                            // If appointment overlaps with this slot, add the doctor to occupied set
                            if (slotStart < aptEnd && slotEnd > aptStart) {
                              occupiedDoctors.add(apt.doctor_id);
                            }
                          } catch (error) {
                            // ignore
                          }
                        });

                        // Slot has conflict only if all doctors are occupied
                        // Since we don't know total doctor count here, we'll allow booking if there are appointments
                        // The actual conflict check should happen in the booking form
                        const hasConflict = false; // Allow clicking to open booking form

                        const hasUnavailability = dayUnavailability.some(
                          (unavail) => {
                            try {
                              const unavailStart = new Date(unavail.start_time);
                              const unavailEnd = new Date(unavail.end_time);
                              return (
                                slotStart < unavailEnd && slotEnd > unavailStart
                              );
                            } catch (error) {
                              return false;
                            }
                          }
                        );

                        const isAvailable = !hasConflict && !hasUnavailability;

                        // Teams/Outlook style: distinct hour lines vs sub-hour lines
                        const isHourLine = slot.minutes === 0;

                        return (
                          <div
                            key={`line-${dayIndex}-${slot.time}`}
                            className={`absolute w-full ${
                              isHourLine
                                ? "border-b border-slate-200"
                                : "border-b border-slate-100 border-dashed"
                            } ${
                              isAvailable && onSlotClick
                                ? "hover:bg-blue-50/50 cursor-pointer transition-colors z-5"
                                : ""
                            }`}
                            style={{
                              top: `${index * (HOUR_HEIGHT / (60 / timeSlotMinutes))}px`,
                              height: `${HOUR_HEIGHT / (60 / timeSlotMinutes)}px`,
                              scrollMarginTop: "200px",
                            }}
                            onClick={() =>
                              isAvailable && handleSlotClick(date, slot.time)
                            }
                            title={
                              isAvailable && onSlotClick
                                ? `Click to book appointment at ${slot.label}`
                                : ""
                            }
                            data-testid={`time-slot-${format(date, "yyyy-MM-dd")}-${slot.time}`}
                          />
                        );
                      })}

                      {/* Current time indicator */}
                      {currentTimePosition && (
                        <div
                          className="absolute w-full z-20 pointer-events-none"
                          style={currentTimePosition}
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <div className="flex-1 h-0.5 bg-red-500"></div>
                            {viewType === "day" && (
                              <div className="text-xs text-red-500 font-medium px-2 bg-white">
                                {format(currentTime, "h:mm a")}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Unavailability blocks */}
                      {dayUnavailability.map((unavail) => (
                        <div
                          key={`unavail-${unavail.id}`}
                          className="absolute left-1 right-1 z-10 bg-gray-200 border-2 border-gray-300 rounded-lg p-1 opacity-80"
                          style={getUnavailabilityStyle(unavail)}
                        >
                          <div className="flex items-center gap-1">
                            <Lock className="w-3 h-3 text-gray-600" />
                            {viewType === "day" && (
                              <span className="text-xs font-medium text-gray-700">
                                {unavail.is_all_day
                                  ? "All Day"
                                  : `${formatTime(unavail.start_time)} - ${formatTime(unavail.end_time)}`}
                              </span>
                            )}
                          </div>
                          {viewType === "day" && (
                            <>
                              <p className="text-xs text-gray-600 mb-1">
                                {unavail.reason || "Unavailable"}
                              </p>
                              <div className="flex gap-1">
                                {onEditUnavailability && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      onEditUnavailability(unavail)
                                    }
                                    className="h-5 w-5 text-blue-600 hover:text-blue-700"
                                  >
                                    <Edit className="w-2 h-2" />
                                  </Button>
                                )}
                                {onDeleteUnavailability && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      onDeleteUnavailability(unavail.id)
                                    }
                                    className="h-5 w-5 text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="w-2 h-2" />
                                  </Button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {/* Appointments */}
                      {dayAppointments.map((appointment, index) => {
                        // Calculate end time
                        const endTimeString = appointment.end_time
                          ? formatTime(
                              `${appointment.date}T${appointment.end_time}:00`
                            )
                          : formatAppointmentEndTime(
                              appointment.appointment_date,
                              appointment.duration_actual || 60
                            );

                        // Get column info for this appointment
                        const columnInfo = appointmentColumns.get(
                          appointment.id
                        );

                        return (
                          <div
                            key={`apt-${appointment.id}`}
                            data-testid={`appointment-block-${index}`}
                            className="absolute z-15 rounded-md p-2 border-l-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                            style={{
                              ...getAppointmentStyle(appointment, columnInfo),
                              ...getAppointmentBackgroundStyle(appointment),
                            }}
                            onClick={() =>
                              onViewAppointment &&
                              onViewAppointment(appointment)
                            }
                          >
                            <div className="flex items-center justify-between h-full overflow-hidden">
                              <div className="flex-1 min-w-0">
                                <AppointmentContent
                                  appointment={appointment}
                                  viewType={viewType}
                                  columnInfo={columnInfo}
                                  allDoctors={allDoctors}
                                  endTimeString={endTimeString}
                                />
                              </div>
                              {appointment.status === "cancelled" &&
                                viewType === "day" &&
                                columnInfo?.totalColumns === 1 && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs ml-2"
                                  >
                                    Cancelled
                                  </Badge>
                                )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Empty state for day view - pointer-events-none allows clicks to pass through to time slots */}
                      {viewType === "day" &&
                        dayAppointments.length === 0 &&
                        dayUnavailability.length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center text-slate-500">
                              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>
                                No appointments or blocks scheduled for this day
                              </p>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeGridCalendar;
